import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "sh_install_dismissed";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

// Subtle, dismissible "Install app" prompt. Adds no app feature — purely an
// optional install affordance for PWA (Chrome/Android) and iOS Safari.
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1") return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS Safari has no beforeinstallprompt — offer the manual instructions.
    let iosTimer;
    if (isIos()) iosTimer = setTimeout(() => setShow(true), 4000);

    return () => { window.removeEventListener("beforeinstallprompt", onBip); clearTimeout(iosTimer); };
  }, []);

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setShow(false); setIosHelp(false); };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* noop */ }
      setDeferred(null);
      dismiss();
    } else if (isIos()) {
      setIosHelp(true);
    }
  };

  if (!show) return null;

  return (
    <div role="dialog" aria-label="Install Sisterhood+" data-testid="install-prompt"
      className="fixed left-1/2 -translate-x-1/2 z-[70] w-[92%] max-w-md rounded-2xl bg-[#291B2D] text-[#FCFAF6] shadow-2xl p-4"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)" }}>
      {!iosHelp ? (
        <div className="flex items-center gap-3">
          <img src="/icon-192.png" alt="" className="h-11 w-11 rounded-xl" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Install Sisterhood+</p>
            <p className="text-xs text-[#FCFAF6]/70">Add to your home screen for a faster, app-like experience.</p>
          </div>
          <button data-testid="install-accept" onClick={install}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#C89B55] text-[#291B2D] px-4 py-2 text-sm font-semibold">
            <Download className="h-4 w-4" /> Install
          </button>
          <button data-testid="install-dismiss" onClick={dismiss} aria-label="Dismiss install prompt"
            className="shrink-0 p-1.5 rounded-full text-[#FCFAF6]/60 hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <p className="font-semibold text-sm">Add to Home Screen</p>
            <p className="text-xs text-[#FCFAF6]/80 mt-1 leading-relaxed">
              Tap the <Share className="inline h-3.5 w-3.5 mx-0.5" /> <strong>Share</strong> button in Safari,
              then choose <strong>“Add to Home Screen.”</strong>
            </p>
          </div>
          <button data-testid="install-dismiss" onClick={dismiss} aria-label="Close"
            className="shrink-0 p-1.5 rounded-full text-[#FCFAF6]/60 hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}
